import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiAlertTriangle, FiTarget, FiClock, FiCalendar, FiHeart } = FiIcons;

const ResultScreen = ({ formData, auditAnswers, onBack }) => {
  // Questions data for calculation
  const questions = [
    { id: 'fuentes_trafico', scoreImpact: 10, stage: 'captura', weakness: 'Personas que preguntan y no reciben respuesta a tiempo' },
    { id: 'formularios_integrados', scoreImpact: 10, stage: 'captura', weakness: 'Gente interesada que nunca llega a agendar una cita' },
    { id: 'crm_centralizado', scoreImpact: 10, stage: 'captura', weakness: 'Información de prospectos que se pierde entre hojas de cálculo' },
    { id: 'velocidad', scoreImpact: 30, stage: 'seguimiento', weakness: 'Personas que preguntan y no reciben respuesta a tiempo' },
    { id: 'persistencia', scoreImpact: 25, stage: 'seguimiento', weakness: 'Conversaciones que se quedan a medias y nadie retoma' },
    { id: 'multicanal_automatizado', scoreImpact: 10, stage: 'seguimiento', weakness: 'Contactos que desaparecen porque solo se intenta en un canal' },
    { id: 'agenda_automatica', scoreImpact: 10, stage: 'conversion', weakness: 'Gente interesada que nunca llega a agendar una cita' },
    { id: 'scripts_estandarizados', scoreImpact: 10, stage: 'conversion', weakness: 'Ventas que dependen del humor o experiencia de la persona' },
    { id: 'recordatorios_automaticos', scoreImpact: 10, stage: 'conversion', weakness: 'Cotizaciones que se olvidan y nunca cierran' },
    { id: 'base_segmentada', scoreImpact: 10, stage: 'fidelizacion', weakness: 'Clientes que compran una vez y luego ya no vuelven' },
    { id: 'seguimiento_postventa', scoreImpact: 10, stage: 'fidelizacion', weakness: 'Clientes que compran una vez y luego ya no vuelven' },
    { id: 'referidos_automatizados', scoreImpact: 10, stage: 'fidelizacion', weakness: 'Boca a boca digital que nunca se aprovecha' }
  ];

  // Calculate score
  let score = 100;
  questions.forEach(q => {
    if (auditAnswers[q.id] === false) {
      score -= q.scoreImpact;
    }
  });
  score = Math.max(0, score);

  // Calculate losses
  const porcentajePerdida = 100 - score;
  const ingresoPotencial = formData.ticketPromedio * formData.leadsMenuales;
  const ingresoPotencialMes = ingresoPotencial * 0.10; // 10% conversion rate
  const dineroPerdidoMes = ingresoPotencialMes * (porcentajePerdida / 100);
  const dineroPerdidoAnual = dineroPerdidoMes * 12;

  // Get unique weaknesses
  const weaknesses = [];
  const seenWeaknesses = new Set();
  questions.forEach(q => {
    if (auditAnswers[q.id] === false && !seenWeaknesses.has(q.weakness)) {
      weaknesses.push(q.weakness);
      seenWeaknesses.add(q.weakness);
    }
  });

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center mb-8">
          <button 
            onClick={onBack}
            className="flex items-center text-orange-400 hover:text-orange-300 transition-colors font-medium"
          >
            <SafeIcon icon={FiArrowLeft} className="text-2xl mr-2" />
            Volver
          </button>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            📊 Tu Diagnóstico de Pérdidas
          </h1>
          <p className="text-xl text-gray-400">
            Análisis de tu situación actual
          </p>
        </div>

        {/* Bloque 1 - Explicación de debilidades */}
        <div className="bg-gray-800/60 backdrop-blur-md rounded-2xl p-8 mb-8 border border-gray-700">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <SafeIcon icon={FiAlertTriangle} className="text-orange-400 mr-3 text-3xl" />
            ¿Dónde estás perdiendo dinero?
          </h2>
          <div className="space-y-4">
            {weaknesses.map((weakness, index) => (
              <div key={index} className="flex items-center bg-red-500/10 rounded-xl p-4 border border-red-500/20">
                <div className="w-2 h-2 bg-red-500 rounded-full mr-4 shadow-[0_0_10px_rgba(239,68,68,0.8)]"></div>
                <p className="text-lg text-white">{weakness}.</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bloque 2 - Dinero que se está perdiendo */}
        <div className="bg-gradient-to-br from-red-900/40 to-red-800/40 backdrop-blur-md rounded-2xl p-8 mb-8 border border-red-500/50 shadow-2xl shadow-red-900/20">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">
            💸 El impacto en tu bolsillo
          </h2>
          <div className="text-center space-y-6">
            <div>
              <p className="text-lg text-gray-300 mb-2">
                Estimamos que podrías estar dejando ir alrededor de
              </p>
              <p className="text-5xl font-bold text-red-400">
                ${Math.round(dineroPerdidoMes).toLocaleString('es-MX')} MXN
              </p>
              <p className="text-lg text-gray-300 mt-2">
                al mes en oportunidades que no se cierran.
              </p>
            </div>
            <div className="border-t border-red-500/20 pt-6">
              <p className="text-lg text-gray-300 mb-2">
                Si esto se repite durante un año, es aproximadamente
              </p>
              <p className="text-4xl font-bold text-red-500">
                ${Math.round(dineroPerdidoAnual).toLocaleString('es-MX')} MXN
              </p>
            </div>
          </div>
        </div>

        {/* Bloque 3 - Asociación con TITAN10X + CTA */}
        <div className="bg-gradient-to-br from-orange-500/10 to-pink-600/10 backdrop-blur-md rounded-2xl p-8 border border-orange-500/40 text-center shadow-xl mb-12">
          <h2 className="text-2xl font-bold text-white mb-6">
            🚀 La solución TITAN10X
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            En TITAN10X instalamos un sistema que responde rápido a las personas interesadas, les da seguimiento automático y te ayuda a convertir más conversaciones en citas y ventas, sin que tengas que estar pegado al celular.
          </p>
          <a href="https://titan10x.com" target="_blank" rel="noopener noreferrer" className="inline-block bg-gradient-to-r from-orange-500 to-pink-600 text-white font-bold py-5 px-12 rounded-2xl text-xl hover:from-orange-600 hover:to-pink-700 transform hover:scale-[1.02] transition-all duration-200 shadow-xl shadow-orange-500/20">
            💰 Quiero dejar de perder prospectos
          </a>
          <p className="text-sm text-gray-400 mt-6">
            Agenda tu consulta gratuita y descubre cómo recuperar estos ingresos
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResultScreen;